import React from 'react';
import { MapPin, Calendar } from 'lucide-react';
import { experience } from '../mock';

const defaultCompanyConfig = { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-400' };

const companyConfig = {
  'TransUnion': { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', dot: 'bg-blue-400' },
  'Mercedes-Benz Research & Development': { bg: 'bg-sky-500/15', text: 'text-sky-400', border: 'border-sky-500/30', dot: 'bg-sky-400' },
  'Manhattan Associates': { bg: 'bg-teal-500/15', text: 'text-teal-400', border: 'border-teal-500/30', dot: 'bg-teal-400' },
  'Chemiasoft Private Limited': defaultCompanyConfig,
};

const Experience = () => {
  return (
    <section id="experience" className="py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom, #020617, #0f172a, #020617)' }}>
      {/* Background accent */}
      <div className="absolute top-1/2 -left-64 w-[600px] h-[600px] bg-emerald-500/4 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-20 reveal">
          <p className="text-xs font-mono text-emerald-400 uppercase tracking-[0.2em] mb-4">// experience</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white">
            The Journey <span className="gradient-text">So Far.</span>
          </h2>
          <p className="text-slate-400 mt-4 text-lg">10+ years across product companies & global consultancies.</p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Gradient timeline line */}
          <div className="absolute left-6 top-2 bottom-0 w-px timeline-line hidden sm:block" />

          <div className="space-y-6">
            {experience.map((job, index) => {
              const config = companyConfig[job.company] || defaultCompanyConfig;
              const initials = job.company.split(' ').map(w => w[0]).join('').slice(0, 2);

              return (
                <div
                  key={job.id}
                  className={`reveal reveal-delay-${Math.min(index + 1, 6)} relative sm:pl-20`}
                >
                  {/* Timeline dot + company initial */}
                  <div className="absolute left-0 top-6 hidden sm:flex flex-col items-center gap-1">
                    <div className={`w-12 h-12 rounded-xl ${config.bg} border ${config.border} flex items-center justify-center font-bold text-xs ${config.text} font-mono shadow-lg`}>
                      {initials}
                    </div>
                    {index < experience.length - 1 && (
                      <div className="w-px flex-1 min-h-[2rem] bg-slate-800" />
                    )}
                  </div>

                  {/* Card */}
                  <div className="group relative bg-slate-900 hover:bg-slate-900/80 border border-slate-800 hover:border-slate-700 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/20 overflow-hidden">
                    {/* Subtle gradient on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${config.bg} opacity-0 group-hover:opacity-60 transition-opacity duration-300 rounded-2xl pointer-events-none`} />

                    <div className="relative z-10">
                      {/* Header row */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-white">{job.role}</h3>
                          <div className={`text-sm font-semibold mt-0.5 ${config.text}`}>{job.company}</div>
                        </div>
                        <div className="flex flex-col gap-1 text-xs text-slate-500 sm:text-right shrink-0">
                          <span className="flex items-center gap-1 sm:justify-end font-mono">
                            <Calendar className="w-3 h-3" />
                            {job.duration}
                          </span>
                          <span className="flex items-center gap-1 sm:justify-end">
                            <MapPin className="w-3 h-3" />
                            {job.location}
                          </span>
                        </div>
                      </div>

                      {/* Highlights */}
                      <ul className="space-y-1.5 mb-4">
                        {job.highlights.map((point, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-400">
                            <span className={`mt-2 w-1 h-1 rounded-full ${config.dot} shrink-0`} />
                            {point}
                          </li>
                        ))}
                      </ul>

                      {/* Skills */}
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-800/80">
                        {job.skills.map((skill) => (
                          <span
                            key={skill}
                            className={`px-2.5 py-1 text-xs font-mono font-medium ${config.bg} ${config.text} border ${config.border} rounded-md`}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
